import { Component } from '@theme/component';

/**
 * Count-up for Winter Arc stat figures (blocks/_arc-stat.liquid).
 *
 * The element is rendered with its final value, so the number is correct without
 * JavaScript, for search engines and for screen readers (which read a visually hidden copy;
 * this element is aria-hidden). When the figure first scrolls into view it counts from zero
 * to the value. Prefixes and suffixes ("90+", "Rs. 99") and thousands separators are kept.
 * With reduced motion, or for values that are not numbers, nothing animates.
 *
 * Attributes:
 *   data-value  the final text, for example "90" or "90+"
 *
 *  Stagger: figures that come into view together (a row on desktop) start one after another in
 * the order they entered (150ms apart); a figure that scrolls in on its own (stacked on mobile) starts at
 * once. Nothing animates before it is on screen, and each figure counts only once.
 */
/** Per list: when the most recent figure was scheduled to start. */
const lastStartByList = new WeakMap();

const STAGGER = 150;

class ArcCount extends Component {
  /** Matches an optional prefix, a number (with separators or decimals) and an optional suffix. */
  static #PATTERN = /^(\D*?)(\d[\d,]*(?:\.\d+)?)(.*)$/s;

  /** A count-up is a reveal people watch, not a UI transition, so it runs longer than the 500ms UI budget. */
  static #DURATION = 1600;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {number} */
  #frame = 0;

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    const parts = this.#parse();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!parts || reduceMotion || this.dataset.counted === 'true') return;

    this.textContent = this.#format(parts, 0);

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#timer = window.setTimeout(() => this.#run(parts), this.#delay());
      },
      { threshold: 0.4 }
    );
    this.#observer.observe(this);
  }

  /**
   * Delay relative to the previous figure in the same list, so only figures that entered
   * together are staggered.
   * @returns {number}
   */
  #delay() {
    const list = this.closest('ul, ol') ?? this.parentElement ?? this;
    const now = performance.now();
    const previous = lastStartByList.get(list) ?? -Infinity;
    const start = Math.max(now, previous + STAGGER);
    lastStartByList.set(list, start);
    return start - now;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    window.clearTimeout(this.#timer);
    cancelAnimationFrame(this.#frame);
  }

  /**
   * @returns {{ prefix: string, target: number, decimals: number, grouped: boolean, suffix: string } | null}
   */
  #parse() {
    const match = (this.dataset.value || '').trim().match(ArcCount.#PATTERN);
    if (!match) return null;

    const [, prefix = '', number = '', suffix = ''] = match;
    // Values with more digits after the number ("24/7", "1-2") read wrong mid-count; leave them still.
    if (/\d/.test(suffix)) return null;
    const target = Number(number.replace(/,/g, ''));
    if (!Number.isFinite(target)) return null;

    return {
      prefix,
      target,
      decimals: number.includes('.') ? number.split('.')[1]?.length ?? 0 : 0,
      grouped: number.includes(','),
      suffix,
    };
  }

  /**
   * @param {{ prefix: string, decimals: number, grouped: boolean, suffix: string }} parts
   * @param {number} value
   */
  #format(parts, value) {
    const fixed = value.toFixed(parts.decimals);
    const text = parts.grouped
      ? Number(fixed).toLocaleString('en-IN', {
          minimumFractionDigits: parts.decimals,
          maximumFractionDigits: parts.decimals,
        })
      : fixed;
    return `${parts.prefix}${text}${parts.suffix}`;
  }

  /**
   * @param {{ prefix: string, target: number, decimals: number, grouped: boolean, suffix: string }} parts
   */
  #run(parts) {
    const start = performance.now();

    /** @param {number} now */
    const step = (now) => {
      const progress = Math.min((now - start) / ArcCount.#DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.textContent = this.#format(parts, parts.target * eased);

      if (progress < 1) {
        this.#frame = requestAnimationFrame(step);
      } else {
        this.textContent = this.dataset.value || '';
        this.dataset.counted = 'true';
      }
    };

    this.#frame = requestAnimationFrame(step);
  }
}

if (!customElements.get('arc-count')) {
  customElements.define('arc-count', ArcCount);
}
